import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { ScheduleService } from "@/features/Assistant/services/scheduleService";
import type { DetailModalData } from "@/features/Assistant/hooks/useScheduleData";

interface ScheduleDetailModalProps {
  detailModal: DetailModalData | null;
  onClose: () => void;
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-3 gap-2 py-2 border-b">
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span className="col-span-2 text-sm">{children}</span>
  </div>
);

/**
 * The meeting URL comes from the calendar, and anyone able to send an invite to the
 * bot's mailbox controls it — so it is only linkable when its scheme is one we accept.
 * Anything else (`javascript:` above all) is shown as plain text instead of a link.
 */
const LINKABLE_PROTOCOLS = ["http:", "https:", "msteams:"];

const linkableUrl = (url: string): string | null => {
  try {
    return LINKABLE_PROTOCOLS.includes(new URL(url).protocol) ? url : null;
  } catch {
    return null;
  }
};

const MeetingLink = ({ url }: { url: string | null }) => {
  if (!url) return <>—</>;

  const safeUrl = linkableUrl(url);
  if (!safeUrl) return <span className="text-sm break-all">{url}</span>;

  return (
    <a
      href={safeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-blue-500 hover:text-blue-600 font-medium underline break-all"
    >
      {safeUrl}
    </a>
  );
};

export const ScheduleDetailModal = ({ detailModal, onClose }: ScheduleDetailModalProps) => {
  const { t } = useTranslation();

  const title =
    detailModal?.type === "meeting"
      ? t("assistant.details.meetingTitle")
      : t("assistant.details.callTitle");

  return (
    <Dialog open={!!detailModal} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {detailModal?.type === "call" && (() => {
            const call = detailModal.data;
            return (
              <>
                <Row label={t("assistant.details.callId")}>
                  <span className="font-mono">{call.callId}</span>
                </Row>
                <Row label={t("assistant.details.subject")}>{call.meetingName || "—"}</Row>
                <Row label={t("assistant.details.workspace")}>{call.workspace || "—"}</Row>
                <Row label={t("assistant.details.startTime")}>
                  {ScheduleService.formatTime(call.startTime ?? "")}
                </Row>
                <Row label={t("assistant.details.meetingUrl")}>
                  <MeetingLink url={call.meetingUrl} />
                </Row>
              </>
            );
          })()}

          {detailModal?.type === "meeting" && (() => {
            const meeting = detailModal.data;
            return (
              <>
                <Row label={t("assistant.details.subject")}>{meeting.meetingName || "—"}</Row>
                <Row label={t("assistant.details.from")}>{meeting.fromEmail || "—"}</Row>
                <Row label={t("assistant.details.workspace")}>{meeting.workspace || "—"}</Row>
                <Row label={t("assistant.details.scheduledTime")}>
                  {ScheduleService.formatTimeRange(
                    meeting.meetingTime ?? "",
                    meeting.meetingEndTime ?? undefined
                  )}
                </Row>
                <Row label={t("assistant.details.recurring")}>
                  {meeting.recurrence
                    ? t(`assistant.details.recurrencePatterns.${meeting.recurrence.split(" ")[0].split(":")[0]}`, {
                        defaultValue: meeting.recurrence,
                      })
                    : meeting.seriesId
                      ? t("assistant.details.yes")
                      : t("assistant.details.no")}
                </Row>
                {meeting.recurrence && (
                  <Row label={t("assistant.details.recurrenceDetail")}>{meeting.recurrence}</Row>
                )}
                {meeting.recurrenceEndsAt && (
                  <Row label={t("assistant.details.recurrenceEndsAt")}>
                    {ScheduleService.formatTime(meeting.recurrenceEndsAt)}
                  </Row>
                )}
                {meeting.reason && (
                  <Row label={t("assistant.schedule.columns.reason")}>
                    <span className="text-destructive">
                      {t(`assistant.schedule.reasons.${meeting.reason}`, {
                        defaultValue: t("assistant.schedule.reasons.unknown"),
                      })}
                    </span>
                  </Row>
                )}
                <Row label={t("assistant.details.transcriptionLanguage")}>
                  {meeting.transcriptionLanguage || "—"}
                </Row>
                <Row label={t("assistant.details.maxWait")}>
                  {meeting.maxWaitMinutes != null
                    ? t("assistant.details.minutes", { count: meeting.maxWaitMinutes })
                    : "—"}
                </Row>
                <Row label={t("assistant.details.meetingUrl")}>
                  <MeetingLink url={meeting.meetingUrl} />
                </Row>
              </>
            );
          })()}

          
        </div>
      </DialogContent>
    </Dialog>
  );
};
